import React, { useEffect, useRef, useState } from "react";
import MassProductViewModel from "@/models/server/MassProductViewModel";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import HttpClient from "@/common/HttpClient";
import EndpointPaths from "@/common/EndpointPaths";
import GetReturnedMassProductRequest from "@/models/server/requests/GetReturnedMassProductRequest";
import { GetReturnedMassProductResponse } from "@/models/server/responses/GetReturnedMassProductResponse";
import { ReturnedMassProductItem } from "@/components/ReturnedMassProductItem";
import { Modal } from "@renta-apps/athenaeum-react-components";
import {FiltersType, ReturnInspectionStatus} from "@/models/Enums";
import ReturnedMassProductHeader from "../ReturnedMassProductHeader/ReturnedMassProductHeader";
import DevicesFiltersModal from "@/components/DevicesFiltersModal/DevicesFiltersModal";
import {covertToGetMassProductListRequest, emptyRequestRawData, RequestRawData} from "@/models/RequestRawData";
import { ReturnMassProductListProps } from "@/components/ReturnedMassProductList/ReturnMassProductList.types";
import GetReturnedMassProductCountResponse from "@/models/server/GetReturnedMassProductCountResponse";
import UserInteractionDataStorage from "@/providers/UserInteractionDataStorage";
import Localizer from "@/localization/Localizer";

const ReturnedMassProductList = ({
  filtersData,
}: ReturnMassProductListProps) => {
  const [request, setRequest] = useState<GetReturnedMassProductRequest>(
    new GetReturnedMassProductRequest(),
  );
  const [data, setData] = useState<MassProductViewModel[] | null>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [extendedView, setExtendedView] = useState<boolean>(false);
  const lastElementRef = useRef<HTMLDivElement>(null);
  const filtersModalRef = useRef<Modal>(null);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [rawRequest, setRawRequest] =
    useState<RequestRawData>(emptyRequestRawData);

  useEffect(() => {
    setIsFetching(true);
    const item = localStorage.getItem(
      RentaToolsConstants.massProductListViewSetting,
    );

    if (item) {
      setExtendedView(item.toLowerCase() == "true");
    }

    const restoredRequest = initializeMassProductRequest();

    const newRawRequest = covertToGetMassProductListRequest(restoredRequest);
    
    setRawRequest(newRawRequest);
    
    setRequest(restoredRequest);

    getItemsCount(restoredRequest).then((r) => setItemsCount(r));
    
    fetchDataAsync(restoredRequest).then((r) => setData(r))
        .finally(() => setIsFetching(false));
  }, []);

  useEffect(() => {
    intersectionObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && !isFetching && haveMoreDataToLoad()) {
        const newRequest = request;
        newRequest.nextPage();

        setIsFetching(true);
        const result = await fetchDataAsync(request);
        setData((prevData) => (prevData ? [...prevData, ...result] : result));
        setIsFetching(false);
        setRequest(newRequest);
      }
    });

    if (intersectionObserver.current && lastElementRef.current) {
      intersectionObserver.current.observe(lastElementRef.current);
    }

    return () => {
      if (intersectionObserver.current && lastElementRef.current) {
        intersectionObserver.current.unobserve(lastElementRef.current);
      }
    };
  }, [isFetching, request]);

  const fetchDataAsync = async (newRequest : GetReturnedMassProductRequest): Promise<MassProductViewModel[]> => {
    const result = await HttpClient.postAsync<GetReturnedMassProductResponse>(
      EndpointPaths.MassProductReturnInspection.GetMassProduct, 
        newRequest,
    );

    if (result.success) {
      return result.massProducts;
    }

    return [];
  };
  
  const getItemsCount = async (newRequest: GetReturnedMassProductRequest): Promise<number> => {
    const result = await HttpClient.postAsync<GetReturnedMassProductCountResponse>(
        EndpointPaths.MassProductReturnInspection.GetReturnedMassProductsCount,
        newRequest,
    );

    if (result.success) {
      return result.count;
    }
    return 0;
  }

  const haveMoreDataToLoad = (): boolean => {
    return (
        request.pageSize *
        (request.pageNumber === 0 ? 1 : request.pageNumber + 1) ===
        data?.length!
    );
  };

  const extendedViewOnChange = () => {
    setExtendedView(!extendedView);
  };

  const initializeMassProductRequest = () : GetReturnedMassProductRequest =>{
    const request = new GetReturnedMassProductRequest();

    request.returnInspectionStatus = [ReturnInspectionStatus.PartlyCompleted.toString(), ReturnInspectionStatus.Waiting.toString()];

    const restoredRequest = UserInteractionDataStorage.restorePersistent("ReturnedMassProductList", request);
    
    restoredRequest.pageNumber = 0;
    restoredRequest.pageSize = RentaToolsConstants.itemsPerReturnInspectionPage;
    restoredRequest.modified = true;
    

    return restoredRequest;
  }

  const submitFiltersAsync = async (
      newRequest: RequestRawData,
  ): Promise<void> => {
    await filtersModalRef.current?.closeAsync();

    const returnInspectionRequest =
        GetReturnedMassProductRequest.create(newRequest);
    returnInspectionRequest.modified = true;
    returnInspectionRequest.resetPage();

    setRequest(returnInspectionRequest);

    UserInteractionDataStorage.setPersistent("ReturnedMassProductList", returnInspectionRequest);

    const newRawRequest = covertToGetMassProductListRequest(returnInspectionRequest);

    setRawRequest(newRawRequest);
    
    getItemsCount(returnInspectionRequest).then((r) => setItemsCount(r));
    
    const result = await fetchDataAsync(returnInspectionRequest);

    setData(result);
  };

  return (
    <div>
      <DevicesFiltersModal modalRef={filtersModalRef}
                           request={rawRequest}
                           filtersData={filtersData}
                           title={Localizer.massProductFiltersTitle}
                           onSubmit={async (submittedRequestData) => submitFiltersAsync(submittedRequestData)}
                           filtersType={FiltersType.MassProductList}
      />
      <ReturnedMassProductHeader extendedViewOnChange={extendedViewOnChange}
                                 onFilterClick={() => filtersModalRef.current?.openAsync()}
                                 itemsCount={itemsCount}
                                 isEmpty={request.isEmpty()}
      />
      {data?.map((item, index) => (
        <ReturnedMassProductItem item={item}
                                 key={index}
                                 index={index}
                                 extendedView={extendedView}
                                 itemsLength={data.length}
                                 lastRef={lastElementRef}
        />
      ))}
    </div>
  );
};


export default ReturnedMassProductList;